import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import {
  ProfileService,
  ProfileStore,
  type UserSession,
  selectCustomerProfileId
} from '@asset-solutions/customer-portal/auth/data-access';
import { clearOrders } from '@asset-solutions/customer-portal/orders/store';
import type { CustomerProfile } from '@asset-solutions/customer-portal/user-management/models';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { concatMap, pipe } from 'rxjs';

@Component({
  selector: 'auth-customer-select',
  imports: [FormsModule, MatInputModule, MatAutocompleteModule, MatIcon, MatIconButton],
  templateUrl: './customer-select.component.html',
  styleUrl: './customer-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerSelectComponent {
  public readonly session = input<UserSession>();
  private readonly store = inject(Store);
  private readonly profileId = this.store.selectSignal(selectCustomerProfileId);
  private readonly profileStore = inject(ProfileStore);
  private readonly profileService = inject(ProfileService);

  protected searchTerm = signal<string | CustomerProfile>('');
  protected customerCount = computed(() => this.allCustomers().length);
  protected filteredCustomers = computed(() => {
    const searchTerm = this.searchTerm();
    return this.allCustomers().filter((customer) => {
      if (typeof searchTerm === 'string') {
        return (
          customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.customerNbrs.some((id) => id.includes(searchTerm))
        );
      } else {
        return customer.customerProfileId === searchTerm.customerProfileId;
      }
    });
  });
  protected selectedCustomer = computed(() => {
    const profileId = this.profileId();
    return this.allCustomers().find((customer) => customer.customerProfileId === profileId);
  });
  private allCustomers = signal<CustomerProfile[]>([]);

  private getCustomers = rxMethod<void>(
    pipe(
      concatMap(() =>
        this.profileService.getAllCustomers().pipe(
          tapResponse({
            next: (customers) => this.allCustomers.set(customers.sort((a, b) => a.name.localeCompare(b.name))),
            error: () => this.allCustomers.set([])
          })
        )
      )
    )
  );

  constructor() {
    effect(() => {
      const customer = this.selectedCustomer();
      if (customer) {
        this.searchTerm.set(customer);
      }
    });
    this.getCustomers();
  }

  protected displayFn(customer: CustomerProfile | string | null): string {
    return typeof customer === 'string' ? customer : customer?.name || '';
  }

  protected selectCustomer({ option }: MatAutocompleteSelectedEvent): void {
    this.store.dispatch(clearOrders());
    this.profileStore.selectCustomer(option.value);
  }
}
